import { TournamentConditionTypeEnum, TournamentUserInfo } from 'config/types/tournament'
import useModal from 'hooks/useModal'
import { RowCenter } from 'layout/Components/Row'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { breakpointMap } from 'theme/base'
import { colors } from 'theme/colors'
import Box from 'UIKit/Box/Box'
import { TableCellHistory } from 'UIKit/Table/TableHistory'
import TableRow from 'UIKit/Table/TableRow'
import Text from 'UIKit/Text'
import { getFullDisplayBalance } from 'utils/formatBalance'
import UserProfileModal from 'views/Profile/components/UserProfileModal'
import { DisplayMultiplier } from 'views/Profile/Transactions/components/styled'
import TournamentReward from '../../../TournamentReward'
import DisplayRank from '../DisplayRank'

const EmptyContent = ({ isActiveUser }: { isActiveUser?: boolean }) => (
  <Text color={isActiveUser ? 'text' : 'textAlt5'} fontSize="12px" fontWeight={600}>
    -
  </Text>
)

const LeaderBoardTableRow: React.FC<
  TournamentUserInfo & {
    conditionType: TournamentConditionTypeEnum
    isActiveUser?: boolean
  }
> = ({ displayName, userCode, prize, rank, multiplier, wagerAmount, conditionType, isActiveUser = false }) => {
  const [onPresentProfilePopup] = useModal(UserProfileModal)

  const handleOpenProfilePopup = () => {
    onPresentProfilePopup({ code: userCode })
  }

  const isUserAvailable = userCode?.length > 0
  const isPublicUser = displayName?.length > 0

  return (
    <StylledTableRow
      style={{ backgroundColor: `${isActiveUser ? colors.primary : ''}`, borderRadius: '4px !important' }}
    >
      <StyledTableCell style={{ display: 'flex', justifyContent: 'center' }}>
        <DisplayRank prize={prize} rank={rank} isActive={isActiveUser} />
      </StyledTableCell>
      <StyledTableCell onClick={isPublicUser ? handleOpenProfilePopup : null}>
        {isUserAvailable ? (
          <>
            {isPublicUser ? (
              <DisplayText style={{ cursor: isPublicUser && 'pointer' }}>{displayName}</DisplayText>
            ) : (
              <RowCenter>
                <StyledUserHiddenAvatar width={16} height={16} mr="4px" $isActiveUser={isActiveUser}>
                  <Icons.HiddenAvatar width={16} height={16} />
                </StyledUserHiddenAvatar>
                <DisplayText>
                  <Trans>Hidden</Trans>
                </DisplayText>
              </RowCenter>
            )}
          </>
        ) : (
          <EmptyContent />
        )}
      </StyledTableCell>

      <StyledTableCell>
        {isUserAvailable ? (
          <DisplayText>
            {conditionType === TournamentConditionTypeEnum.Wager ? (
              `$${getFullDisplayBalance(wagerAmount)}`
            ) : (
              <DisplayMultiplier amount={multiplier} />
            )}
          </DisplayText>
        ) : (
          <EmptyContent />
        )}
      </StyledTableCell>

      <StyledTableCell>
        {prize ? (
          <StyledTournamentReward rewardDetails={prize} fontSize="12px" textAlign="left" $isActive={isActiveUser} />
        ) : (
          <EmptyContent isActiveUser={isActiveUser} />
        )}
      </StyledTableCell>
    </StylledTableRow>
  )
}
const StylledTableRow = styled(TableRow)`
  border-radius: 4px !important;
  overflow: hidden;
`

const StyledUserHiddenAvatar = styled(Box)<{ $isActiveUser }>`
  svg {
    path {
      fill: ${({ $isActiveUser }) => ($isActiveUser ? '#fff' : '')};
    }
  }
`

const StyledTournamentReward = styled(TournamentReward)<{ $isActive }>`
  & > * {
    font-size: 12px;
    color: ${({ theme: { colors }, $isActive }) => $isActive && colors.text};
  }
`

const StyledTableCell = styled(TableCellHistory)`
  padding: 6px 12px;
  min-height: 36px;
`

const DisplayText = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.24px;
  color: ${({ theme: { colors } }) => colors.textTertiary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: ${breakpointMap.sm}px) {
    max-width: 78px;
  }
`

export default LeaderBoardTableRow
